import RadioOptions from "../services/models/radioOptions.model";

export const SCC_CLINICAL_INFO_HEADING =
  "CLINICAL INFORMATION (REQUIRED) (This product is validated for patients with one or more high-risk features. Please check all that apply from both columns.)";

export const YES_NO_OPTIONS: RadioOptions[] = [
  { id: "Yes", label: "Yes", value: "Yes" },
  { id: "No", label: "No", value: "No" },
];

export const TUMOR_DIAMETER_LT2CM = "<2cm";
export const TUMOR_DIAMETER_2_TO_4CM = "2cm - < 4cm";
export const TUMOR_DIAMETER_GTE4CM = "≥ 4cm";

export const TUMOR_DIAMETER_LT2CM_LABEL = "<2cm";
export const TUMOR_DIAMETER_2_TO_4CM_LABEL = "2cm - < 4cm";
export const TUMOR_DIAMETER_GTE4CM_LABEL = "≥ 4cm";

export const TUMOR_DIAMETER_OPTIONS: RadioOptions[] = [
  {
    id: "lt2cm",
    label: TUMOR_DIAMETER_LT2CM_LABEL,
    value: TUMOR_DIAMETER_LT2CM,
  },
  {
    id: "2to4cm",
    label: TUMOR_DIAMETER_2_TO_4CM_LABEL,
    value: TUMOR_DIAMETER_2_TO_4CM,
  },
  {
    id: "gte4cm",
    label: TUMOR_DIAMETER_GTE4CM_LABEL,
    value: TUMOR_DIAMETER_GTE4CM,
  },
];

export function getTumorDiameterLabel(value: string): string {
  const option = TUMOR_DIAMETER_OPTIONS.find((item) => item.value === value);
  return option?.label ?? value;
}

export const SCC_REQUIRED_HISTORY_QUESTIONS = [
  {
    name: "historyPhysicalExam.isPatientImmunocompromised",
    label:
      "Is the patient immunocompromised due to solid organ transplant, lymphoma/leukemia, or HIV?",
    options: YES_NO_OPTIONS,
  },
  {
    name: "historyPhysicalExam.isTumorLocatedOnHeadOrNeck",
    label: "Is the tumor located on the head or neck?",
    options: YES_NO_OPTIONS,
  },
  {
    name: "historyPhysicalExam.tumorDiameter",
    label: "What is the tumor's diameter?",
    options: TUMOR_DIAMETER_OPTIONS,
  },
] as const;

export const SCC_REQUIRED_PATHOLOGY_QUESTIONS = [
  {
    name: "pathologySurgicalFindings.isPerineuralInvolvement",
    label: "Is there perineural involvement (any size)?",
    options: YES_NO_OPTIONS,
  },
  {
    name: "pathologySurgicalFindings.isPoorlyDifferentiated",
    label: "Is it poorly differentiated?",
    options: YES_NO_OPTIONS,
  },
] as const;

export const SCC_ADDITIONAL_HISTORY_OPTIONS: RadioOptions[] = [
  {
    id: "located-h-m",
    label:
      'Tumor location on the hands, genitals, feet or pretibial surface <br/> <em class="text-xs">(areas H or M)</em>',
    value: "Located on Areas H or M",
  },
  {
    id: "rapidly-growing",
    label: "Rapidly growing tumor",
    value: "Rapidly growing tumor",
  },
  {
    id: "poorly-defined-borders",
    label: "Tumor with poorly defined borders",
    value: "Borders poorly defined",
  },
  {
    id: "prior-radiation",
    label:
      "Tumor at site of prior radiation therapy or chronic inflammation",
    value:
      "Tumor at site of prior radiation therapy or chronic inflammatory process",
  },
  {
    id: "neurologic-symptoms",
    label: "Neurologic symptoms in region of tumor",
    value: "Neurological symptoms in tumor region",
  },
];

export const SCC_ADDITIONAL_PATHOLOGY_OPTIONS: RadioOptions[] = [
  {
    id: "depth",
    label:
      'Depth <br/> <em class="text-xs">(invasive beyond subcutaneous fat or Invasion beyond 2mm or Clark Level IV)</em>',
    value: "Depth: Invasive beyond subcutaneous fat or ≥Clark Level IV or >2mm",
  },
  {
    id: "aggressive-subtype",
    label: "Aggressive histologic subtype<sup>A</sup>",
    value: "Specific high-risk sub-types",
  },
  {
    id: "lymphovascular",
    label: "Lymphovascular invasion",
    value: "Lymphatic or vascular involvement",
  },
  {
    id: "desmoplastic",
    label: "Desmoplastic SCC",
    value: "Desmoplastic SCC",
  },
];

export const SCC_AGGRESSIVE_SUBTYPE_FOOTNOTE =
  "^A Acantholytic (adenoid), adenosquamous (showing mucin production), or carcinosarcomatous (metaplastic) subtypes (others will be considered on a case-by-case basis)";

/** Remove banner component and this constant after this date (90 days from ship). */
export const SCC_INTEGRATED_TEST_BANNER_END_DATE = new Date(
  "2026-10-06T23:59:59"
);

export const SCC_INTEGRATED_TEST_BANNER_TEXT =
  "Now available, the DecisionDx-SCC integrated test result! The integrated test combines the 40-gene expression profile with clinicopathologic risk factors and optimized nested predictive models to deliver refined risk-assessment and treatment guidance for your high-risk SCC patients.";

export const SCC_LOW_RISK_DISCLAIMER =
  "Since all clinicopathologic factors are 'no' and '<2cm', please select a high-risk factor to ensure the patient is eligible for the DecisionDx-SCC integrated test.";

export const SCC_REQUIRED_FIELD_LABELS: Record<string, string> = {
  isPatientImmunocompromised:
    "Is the patient immunocompromised due to solid organ transplant, lymphoma/leukemia, or HIV?",
  isTumorLocatedOnHeadOrNeck: "Is the tumor located on the head or neck?",
  tumorDiameter: "What is the tumor's diameter?",
  isPerineuralInvolvement: "Is there perineural involvement (any size)?",
  isPoorlyDifferentiated: "Is it poorly differentiated?",
};

export const SCC_TOUCHED_FIELD_PATHS = [
  "historyPhysicalExam.isPatientImmunocompromised",
  "historyPhysicalExam.isTumorLocatedOnHeadOrNeck",
  "historyPhysicalExam.tumorDiameter",
  "historyPhysicalExam.additionalHistoryPhysicalExam",
  "pathologySurgicalFindings.isPerineuralInvolvement",
  "pathologySurgicalFindings.isPoorlyDifferentiated",
  "pathologySurgicalFindings.additionalPathologySurgicalFindings",
];

const sccClinicalInfoConfig = {
  heading: SCC_CLINICAL_INFO_HEADING,
  requiredHistoryQuestions: SCC_REQUIRED_HISTORY_QUESTIONS,
  requiredPathologyQuestions: SCC_REQUIRED_PATHOLOGY_QUESTIONS,
  additionalHistoryOptions: SCC_ADDITIONAL_HISTORY_OPTIONS,
  additionalPathologyOptions: SCC_ADDITIONAL_PATHOLOGY_OPTIONS,
  aggressiveSubtypeFootnote: SCC_AGGRESSIVE_SUBTYPE_FOOTNOTE,
  requiredFieldLabels: SCC_REQUIRED_FIELD_LABELS,
  touchedFieldPaths: SCC_TOUCHED_FIELD_PATHS,
};

export default sccClinicalInfoConfig;
